import type Owner from '@ember/owner';
import Route from '@ember/routing/route';
import RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import * as Sentry from '@sentry/ember';
import type IntlService from 'ember-intl/services/intl';
import config from 'walden-fast/config/environment';
import {
  COOKIE_LOCAL_STORAGE_KEY,
  COOKIES,
} from 'walden-fast/constants/cookies';
import ClarityService from 'walden-fast/services/clarity';
import PostHogService from 'walden-fast/services/posthog';
import worker from 'walden-fast/utils/msw';
import schema from 'walden-fast/utils/graphql/schema.graphql';

export default class ApplicationRoute extends Route {
  @service declare router: RouterService;
  @service('intl') declare intlService: IntlService;
  @service declare clarity: ClarityService;
  @service declare posthog: PostHogService;

  async beforeModel() {
    const storedCookies = window.localStorage.getItem(COOKIE_LOCAL_STORAGE_KEY);
    if (storedCookies) {
      const cookies = JSON.parse(storedCookies) as string[];

      if (cookies.includes(COOKIES.ANALYTICAL)) {
        this.clarity.start();
        this.posthog.start();
      }
    }

    this.setupIntl();
    this.setupSentry();
    await this.setupMsw();
  }

  setupIntl() {
    this.intlService.setLocale(config.defaultLocale);
  }

  setupSentry() {
    Sentry.setContext('app', {
      locale: this.intlService.primaryLocale,
    });
  }

  async setupMsw() {
    if (config.environment === 'test' || !config.msw.isEnabled) {
      return;
    }
    try {
      await worker.start(schema);
      console.warn('MSW started successfully');
    } catch (error) {
      console.error('MSW setup failed:', error);
      // Don't throw - let the app continue without MSW
    }
  }

  constructor(owner: Owner) {
    super(owner);
    this.router.on('routeDidChange', () => {
      this.clarity.trackPage();
    });
  }
}
